import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Eye } from "lucide-react"

import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Item {
  id: number
  type: "mileage" | "expense"
  date: string | null
  case_name: string | null
  case_id: number | null
  employee: string | null
  miles?: number
  rate?: number
  expense_type?: string | null
  description?: string | null
  amount: number
  paid_at: string | null
  paid_reference: string | null
}

interface Props {
  employee_name: string
  paid_date: string
  payment_reference: string
  mileage_items: Item[]
  expense_items: Item[]
  mileage_total: number
  expense_total: number
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function viewHref(row: Item): string | undefined {
  if (!row.case_id) return undefined
  return row.type === "mileage"
    ? `/cases/${row.case_id}/time_entries/${row.id}`
    : `/cases/${row.case_id}/expenses/${row.id}`
}

/* ── View link column ──────────────────────────────────── */

const viewColumn: HpiColumn<Item> = {
  header: "",
  width: "max-w-[40px]",
  render: (row) => {
    const href = viewHref(row)
    if (!href) return null
    return (
      <Link
        href={href}
        className="text-hpi-active-blue hover:text-hpi-blue-dark"
        title="View details"
      >
        <Eye className="h-4 w-4" />
      </Link>
    )
  },
}

/* ── Columns ───────────────────────────────────────────── */

const mileageColumns: HpiColumn<Item>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Miles",
    width: "max-w-[80px]",
    render: (row) => <div className="text-right text-sm">{row.miles ?? 0}</div>,
  },
  {
    header: "Rate",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{formatCurrency(row.rate ?? 0)}</div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  viewColumn,
]

const expenseColumns: HpiColumn<Item>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Case",
    render: (row) => (
      <div className="text-sm">
        {row.case_name ?? "-"}
        {row.expense_type ? ` — ${row.expense_type}` : ""}
      </div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  viewColumn,
]

/* ── Main Component ────────────────────────────────────── */

export default function HistoryDetail({
  employee_name,
  paid_date,
  payment_reference,
  mileage_items,
  expense_items,
  mileage_total,
  expense_total,
  start_date,
  end_date,
}: Props) {
  const grandTotal = mileage_total + expense_total

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Accounting", href: "/accounting/expenses" },
    {
      title: "Expense History",
      href: `/accounting/expenses/history?start_date=${start_date}&end_date=${end_date}`,
    },
    { title: employee_name, href: "#" },
  ]

  const backUrl = `/accounting/expenses/history?start_date=${start_date}&end_date=${end_date}`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Payment Detail — ${employee_name}`} />

      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <Link
            href={backUrl}
            className="text-hpi-active-blue hover:text-hpi-blue-dark mb-4 inline-flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to History
          </Link>
          <h2 className="text-hpi-dark-gray text-xl font-semibold">
            {employee_name}
          </h2>
          <div className="text-hpi-dark-gray mt-1 flex gap-6 text-sm">
            <span>
              <span className="font-medium">Paid on:</span>{" "}
              {formatDate(paid_date)}
            </span>
            {payment_reference && (
              <span>
                <span className="font-medium">Ref:</span> {payment_reference}
              </span>
            )}
          </div>
        </div>

        {/* Mileage section */}
        {mileage_items.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<Item>
              title="Reimbursed Mileage"
              columns={mileageColumns}
              data={mileage_items}
              keyFn={(row) => row.id}
            />
            <div className="mt-1 text-right text-sm font-medium">
              Mileage Total: {formatCurrency(mileage_total)}
            </div>
          </div>
        )}

        {/* Cash Expenses section */}
        {expense_items.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<Item>
              title="Cash Expenses &amp; Per Diem"
              columns={expenseColumns}
              data={expense_items}
              keyFn={(row) => row.id}
            />
            <div className="mt-1 text-right text-sm font-medium">
              Expense Total: {formatCurrency(expense_total)}
            </div>
          </div>
        )}

        {/* Grand total */}
        {mileage_items.length > 0 && expense_items.length > 0 && (
          <div className="border-hpi-light-gray text-hpi-dark-gray rounded-lg border bg-white p-4 text-right text-base font-semibold">
            Grand Total: {formatCurrency(grandTotal)}
          </div>
        )}

        {mileage_items.length === 0 && expense_items.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No items found for this payment.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
